import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

/**
 * BrandTabs
 *
 * ONE branded, accessible tab component (role=tablist/tab/tabpanel, roving
 * tabindex, arrow keys / Home / End). Two looks from the same family:
 * - 'folder': rounded top corners; the active tab joins the panel below
 *   (give the panel the matching `surface` and a border-x/border-b).
 * - 'pill': fully rounded chips for filter-like choices.
 * - 'underline': flat tabs on a baseline, the active one underlined (compact
 *   secondary rail inside a panel).
 * Uses the theme's primary / secondary tokens only (white-label safe).
 */

export interface BrandTabItem<T extends string> {
  id: T;
  label: ReactNode;
  /** Optional leading icon / icons (decorative; label carries the name) */
  icon?: ReactNode;
  /** Accessible name when the visible label is not enough */
  ariaLabel?: string;
  /** Native tooltip */
  title?: string;
  /**
   * Visibly quieter (gray-400) while not selected, but still fully clickable.
   * Pair with `description` so the reason is not colour-only.
   */
  muted?: boolean;
  /** Accessible description (aria-describedby) and tooltip fallback */
  description?: string;
}

export type BrandTabsVariant = 'folder' | 'pill' | 'underline';
export type BrandTabsSurface = 'white' | 'muted';

interface BrandTabsProps<T extends string> {
  tabs: BrandTabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Accessible name of the tablist */
  ariaLabel: string;
  /** Unique prefix: ties tabs to their panels (see getTabId / getPanelId) */
  idPrefix: string;
  variant?: BrandTabsVariant;
  size?: 'sm' | 'md';
  /** Background of the panel the active folder tab joins */
  surface?: BrandTabsSurface;
  className?: string;
}

export const getTabId = (idPrefix: string, id: string): string => `${idPrefix}-tab-${id}`;
export const getPanelId = (idPrefix: string, id: string): string => `${idPrefix}-panel-${id}`;

export const BrandTabs = <T extends string>({
  tabs,
  value,
  onChange,
  ariaLabel,
  idPrefix,
  variant = 'folder',
  size = 'md',
  surface = 'white',
  className,
}: BrandTabsProps<T>) => {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const select = (index: number): void => {
    const target = tabs[(index + tabs.length) % tabs.length];
    if (!target) return;
    onChange(target.id);
    refs.current[(index + tabs.length) % tabs.length]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        select(index + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        select(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        select(0);
        break;
      case 'End':
        event.preventDefault();
        select(tabs.length - 1);
        break;
      default:
    }
  };

  const isFolder = variant === 'folder';
  const isUnderline = variant === 'underline';

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className={cn(
        'flex',
        isFolder || isUnderline
          ? 'flex-nowrap items-end gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden shadow-[inset_0_-1px_0_rgb(229,231,235)]'
          : 'flex-wrap items-center gap-1.5',
        className
      )}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === value;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={getTabId(idPrefix, tab.id)}
            aria-selected={isActive}
            aria-controls={getPanelId(idPrefix, tab.id)}
            aria-label={tab.ariaLabel}
            aria-describedby={tab.description ? `${getTabId(idPrefix, tab.id)}-desc` : undefined}
            title={tab.title ?? tab.description}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 font-medium',
              'transition-colors duration-150 focus:outline-none',
              isUnderline
                ? [
                    'gap-1 border-b-2 px-2 text-xs h-9 max-sm:h-[44px]',
                    isActive
                      ? 'border-primary-400 text-gray-800 font-semibold focus-visible:border-primary-700'
                      : cn(
                          'border-transparent focus-visible:border-primary-300',
                          tab.muted
                            ? 'text-gray-500 hover:text-gray-700'
                            : 'text-gray-600 hover:text-gray-800'
                        ),
                  ]
                : isFolder
                ? [
                    'rounded-t-lg border border-b-0 border-t-2',
                    size === 'sm' ? 'px-2.5 py-1 text-xs max-sm:min-h-[44px]' : 'px-4 py-2 text-sm',
                    isActive
                      ? cn(
                          'border-gray-200 border-t-primary-400 text-gray-800 font-semibold focus-visible:border-t-primary-700 focus-visible:underline focus-visible:underline-offset-4',
                          surface === 'muted' ? 'bg-gray-100' : 'bg-white'
                        )
                      : 'border-gray-200 border-t-transparent bg-secondary-50 text-gray-700 hover:bg-secondary-100 focus-visible:border-t-primary-400 focus-visible:bg-secondary-100',
                  ]
                : [
                    'rounded-full border',
                    size === 'sm' ? 'px-2 py-1 text-xs max-sm:min-h-[44px]' : 'px-3 py-1.5 text-xs max-sm:min-h-[44px]',
                    isActive
                      ? 'bg-secondary-500 border-secondary-500 text-secondary-contrast focus-visible:bg-secondary-700 focus-visible:text-secondary-700-contrast focus-visible:border-secondary-800 focus-visible:underline focus-visible:underline-offset-2'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 focus-visible:border-primary-400',
                  ]
            )}
          >
            {tab.icon}
            {tab.label}
            {tab.description && (
              <span id={`${getTabId(idPrefix, tab.id)}-desc`} hidden>
                {tab.description}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

interface BrandTabPanelProps<T extends string> {
  idPrefix: string;
  id: T;
  children: ReactNode;
  className?: string;
}

/** The panel of the active tab (render only for the active id). */
export const BrandTabPanel = <T extends string>({
  idPrefix,
  id,
  children,
  className,
}: BrandTabPanelProps<T>) => (
  <div
    role="tabpanel"
    id={getPanelId(idPrefix, id)}
    aria-labelledby={getTabId(idPrefix, id)}
    tabIndex={0}
    className={cn('focus:outline-none', className)}
  >
    {children}
  </div>
);
